<template>
  <div class="ecg-chart" :v-if="currentTime">
    <svg ref="svg" :width="_size.width" :height="_size.height">
      <defs>
        <linearGradient :id="'ecg-gradient-'+this._uid">
          <stop offset="0%" style="stop-color:rgb(255,255,255);stop-opacity:0" />
          <stop offset="100%" style="stop-color:rgb(0,0,0);stop-opacity:0.05" />
        </linearGradient>
      </defs>
      <clipPath :id="'ecg-clip-'+this._uid">
        <rect
          :width="contentSize.width"
          :height="contentSize.height"
          :x="margin.left"
          :y="margin.top"
        />
      </clipPath>
    </svg>
    <div v-if="controls" class="controls">
      <div class="action-button run-button active" :class="playState" @click="togglePause"></div>
      <div class="speeds">
        <div class="speed-btn" :class="{active: currentPlaySpeed===speed.value}" v-for="speed in playSpeeds" :key="speed.label" @click="() => setSpeed(speed.value)">{{speed.label}}</div>
      </div>
    </div>
  </div>
</template>

<script>
import d3 from '@/utils/d3importer'
import widgetContent from '@/mixins/widgetContent'
import {formatValue} from '@/utils'

function prettyTime(seconds){
  // return '00:'+Math.round(seconds*100)
  return Math.round(seconds*1000)+'ms'
}
export default {
  name: "ECGChart",
  mixins: [widgetContent],
  props:{
    data: Array,
    currentTime: Number,
    width: Number,
    height: Number,
    controls: Boolean,
    largeHandle: Boolean,
    showTooltip: Boolean,
    xAxis: Boolean,
    yAxis: Boolean,
    forcedMargin: Object,
    speeds: Array,
    currentPlaySpeed: Number,
  },
  model:{
    prop: 'currentTime',
    event: 'change'
  },
  data () {
    return {
      playState: 'paused'
    }
  },
  mounted () {
    this.x = d3.scaleLinear()
    this.y = d3.scaleLinear()
    this.timeKey = 'Time'
    if(this.data){
      this.updateChart()
      this.updateHandle()
    }
  },

  methods:{
    setSpeed (s) {
      // this.currentPlaySpeed = s
      this.$emit('changePlaySpeed', s)
    },
    togglePause () {
      if(this.playState === 'paused'){
        this.playState = 'playing'
        this.lastUpdate = Date.now()
        this.playLoop()
      } else {
        this.playState = 'paused'
      }
      this.$emit('changePlayStatus', this.playState)
    },
    pause() {
      this.playState = 'paused'
      this.$emit('changePlayStatus', this.playState)
    },
    playLoop(){
      // / Date.now() / dt and whatnot
      // / Ver este throttle (35) del Dashboard...
      
    
      if(this.playState === 'playing'){
        
        let dt = (Date.now()-this.lastUpdate)/1000
        dt = dt*this.currentPlaySpeed
        if(this.currentTime+dt<this.x.domain()[1]) this.$emit('change',this.currentTime+dt)
        else this.$emit('change',0)
        // setTimeout(()=>{
          //   this.playLoop()
        // },1000/120)
        this.lastUpdate = Date.now()
        requestAnimationFrame(this.playLoop)
      } 
    },
    updateChart(){
      this.updateTimeKey()
      let colors = ['#fa0','#08f','#0fa']
      let allLines = [this.ecgLine1,this.ecgLine2,this.ecgLine3].flat()
      let x = this.x.range([0,this.contentSize.width]).domain(d3.extent(allLines,(d)=>+d.dt))
      let y = this.y.range([this.contentSize.height,0]).domain(d3.extent(allLines,(d)=>+d.value))
      
      let ext = y.domain()[1] - y.domain()[0]
      y.domain([y.domain()[0]-ext*0.1,y.domain()[1]+ext*0.1])
      
      let svg = d3.select(this.$refs.svg)
      let root = svg.selectAll('.root').data([null])
      root = root.merge(root.enter().append('g').attr('class','root'))
      
      if(this.xAxis){        
        let xAxis = root.selectAll('.x-axis').data([null])
        let xAxisEnter = xAxis.enter().append('g').attr('class','axis x-axis')
        
        xAxis = xAxis.merge(xAxisEnter)
        xAxis.call(d3.axisBottom(x)).attr('transform',`translate(${this.margin.left},${this.contentSize.height})`);
        xAxis.select('.label').attr("transform", `translate(${this.contentSize.width/2},${this.margin.bottom-10})`)
      }
      
      if(this.yAxis){
        let yAxis = root.selectAll('.y-axis').data([null])
        let yAxisEnter = yAxis.enter().append('g').attr('class','axis y-axis')
        yAxisEnter.append('text').attr('class','label').text('ECG')
      
        yAxis = yAxis.merge(yAxisEnter)
        yAxis.call(d3.axisLeft(y).ticks(0)).attr('transform',`translate(${this.margin.left},${this.margin.top})`);
        yAxis.select('.label').attr("transform", `translate(-${this.margin.left - 30},${this.contentSize.height/2}) rotate(-90)`)
      }
      
      
      let grid = root.selectAll('.grid').data([null])
      let gridEnter = grid.enter().append('g').attr('class','grid')
      gridEnter.selectAll('.x-line').data(x.ticks(10)).enter().append('line').attr('class','x-line')
      gridEnter.selectAll('.y-line').data(y.ticks(6)).enter().append('line').attr('class','y-line')
      grid = grid.merge(gridEnter)
      grid.selectAll('.y-line').attr('x2',this.contentSize.width).attr('y1',(d)=>y(d)).attr('y2',(d)=>y(d))
      grid.selectAll('.x-line').attr('y2',this.contentSize.height).attr('x1',(d)=>x(d)).attr('x2',(d)=>x(d))
      
      
      let content = root.selectAll('.content').data([null])
      content = content.merge(content.enter().append('g').attr('class','content'))
      
      content
        .attr('transform',`translate(${this.margin.left},${this.margin.top})`)
        
      grid.attr('transform',`translate(${this.margin.left},${this.margin.top})`)
      
      
      let items = content.selectAll('.line').data([this.ecgLine1,this.ecgLine2,this.ecgLine3])
      items = items.merge(items.enter().append('path').attr('class','line'))
      
      
      let line = d3.line()
        .x(function(d) { return x(+d.dt); })
        .y(function(d) { return y(+d.value); });
      items
        .attr('d',line)
        .attr('stroke-width',2)
        .attr('stroke',(d,i)=>{
          return colors[i]
        })      
    },
    updateHandle (){
      let svg = d3.select(this.$refs.svg)
      let root = svg.selectAll('.root').data([null])
      
      let self = this
      let x = this.x
      let y = this.y
      
      let sensor = root.selectAll('.sensor').data([null])
      let sensorEnter = sensor.enter().append('g').attr('class','sensor')
      sensorEnter.append('rect').attr('fill','transparent')

      sensorEnter.call(d3.drag().on("start drag", function () {
        let ex = d3.event.x
        
        //GUARRADA MAXIMA FIREFOX... :O=
        if(navigator.userAgent.toLowerCase().indexOf('firefox') > -1 && root.node().parentNode.parentNode.parentNode.className === 'charts-wrapper'){
          let closest = root.node().closest('.vue-grid-item').style.transform
          let dx = closest.replace('translate3d','').replace('(','').replace(')','').split(',')[0].replace('px','')
          ex-=dx
        }
        
        let dragX = Math.max(x.range()[0]+self.margin.left,Math.min(x.range()[1]+self.margin.left,ex))
        self.pause()
        self.$emit('change',x.invert(dragX-self.margin.left))
        // d3.select(this)
        //   .attr('transform',`translate( ${dragX}, 0)`)
      }))
      sensor = sensor.merge(sensorEnter)
      
      sensor.select('rect').attr('width',this.contentSize.width).attr('height',this.contentSize.height)
      sensor.select('rect').attr('transform',`translate(${this.margin.left},${this.margin.top})`)
      
      let current = d3.select(this.$el).selectAll('.current').data([null])
      let currentEnter = current.enter().append('div').attr('class','current '+(this.largeHandle ? 'large' : '')).style('position','absolute')
      currentEnter.append('div').attr('class','circle')
      currentEnter.append('div').attr('class','inner-circle')
      if(this.showTooltip) currentEnter.append('div').attr('class','tooltip').append('div').attr('class','text')
      current = current.merge(currentEnter)
      

      let past = root.selectAll('.past').data([null])
      past = past.merge(past.enter().append('rect').attr('class','past').attr('fill',`url(#ecg-gradient-${this._uid})`))
      past.attr('clip-path',`url(#ecg-clip-${this._uid})`)
      
      // UPDATES
      
      past.attr('width',200).attr('height',this._size.height)
      past.attr('x',x(this.currentTime)+self.margin.left-200)
      
      let nearestDataPoint = this.ecgLine2.find((d)=>d.dt>this.currentTime)
      let currentValue = nearestDataPoint?nearestDataPoint.value:0
      current.style('transform',`translate(${x(this.currentTime)+self.margin.left}px,${y(currentValue)}px)`)
      
      current.select('.tooltip').select('.text').text(prettyTime(this.currentTime))
    },
    updateTimeKey () {
      if (this.data && 'T' in this.data[0]){
        this.timeKey = 'T'
      } else if (this.data && 'time' in this.data[0]){
        this.timeKey = 't'
      }
      else if (this.data && 'Time' in this.data[0]){
        this.timeKey = 'Time'
      }
    }
  },
  computed:{
    _size () {
      let w = this.width || this.size.width
      let h = this.height || this.size.height
      let controlsWidth = this.controls?(h+40):0
      return {
        width: Math.max(0,w - controlsWidth),
        height: Math.max(0,h)
      }
    },
    playSpeeds(){
      // [{value:0.5,label:'0.5x'},{value:0.1,label:'0.1x'},{value:0.01,label:'0.01x'}]
      return this.speeds?this.speeds.map((d)=>({value:d,label:d+'x'})):[]
    },
    contentSize () {
      return {
        width: Math.max(0,this._size.width - this.margin.left - this.margin.right),
        height: Math.max(0,this._size.height  - this.margin.top - this.margin.bottom)
      }
    },
    margin () {
      let m = {left: 0, top: 0, bottom: 0, right: 0}
      if(this.xAxis) m.bottom = 40
      if(this.yAxis) m.left = 70
      if(this.forcedMargin){
        Object.keys(this.forcedMargin).forEach((k)=>m[k] = this.forcedMargin[k])
      }
      return m
      
    },
    skip(){
      return Math.floor(this.data.length/1500) || 1
    },
    ecgLine1 () {
      return this.data && this.data.filter((d,i)=>i%this.skip===0).map((d)=>({dt:d[this.timeKey],value:d.L1}))
    },
    ecgLine2 () {
      return this.data && this.data.filter((d,i)=>i%this.skip===0).map((d)=>({dt:d[this.timeKey],value:d.L2}))
    },
    ecgLine3 () {
      return this.data && this.data.filter((d,i)=>i%this.skip===0).map((d)=>({dt:d[this.timeKey],value:d.L3}))
    },
    
  },
  watch: {
    currentTime () {
      this.updateHandle()
    },
    data () {

      this.updateChart()
      this.updateHandle()
    },
    _size () {
      if(this.data){
        this.updateChart()
        this.updateHandle()
      }
    },
    margin () {
      if(this.data){
        this.updateChart()
        this.updateHandle()
      }
    }
  }
}
</script>

<style scoped lang="stylus">
.ecg-chart
  display: flex
  height: 100%
  position: relative
  // overflow hidden //no vale para los tooltips que se salen!
  width: 100%

  svg
    background: #fff

    // position: absolute;
    >>> .root
      .line
        fill: none
        pointer-events: none

      .past
        pointer-events: none
        // fill rgba(0,0,0,0.1)

      .grid
        line
          stroke: #f0f0f0
          stroke-width: 1px

      .axis
        .tick
          line
            stroke: #aaa

          text
            fill: #aaa

        .domain
          stroke: #aaa

        .label
          fill: #000
          font-size: 12px
          text-anchor: middle

  >>> .current
    left: 0
    pointer-events: none
    position: absolute
    top: 0

    .circle
      display: none

    .inner-circle
      background: #f00
      border-radius: 4px
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.2)
      height: 8px
      position: absolute
      transform: translate(-50%, -50%)
      width: 8px

    .tooltip
      // border solid 1px
      background: #fff
      bottom: 20px
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.2)
      left: 0
      padding: 2px 7px
      position: absolute
      transform: translate(-50%, 0)
      white-space: nowrap
      m-tooltip-tip()

    &.large
      .circle
        background: #fff
        border-radius: 10px
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.2)
        display: block
        height: 20px
        position: absolute
        transform: translate(-50%, -50%)
        width: 20px

      .inner-circle
        background: #f00
        border-radius: 3px
        height: 6px
        position: absolute
        transform: translate(-50%, -50%)
        width: 6px

  .controls
    align-items: center
    display: flex
    flex: 0 0 110px
    justify-content: flex-end
    position: relative

    .action-button
      flex 0 0 50px
      margin-right 10px
      &.disabled
        
      &.paused
        m-icon("action-play", 50, "px")
        width auto

      &.playing
        m-icon("action-pause", 50, "px")
        width auto
    .speeds
      display flex
      flex-direction column
      flex 0 0 auto
      margin-right 2px
      .speed-btn
        
        border-radius 10px
        text-align center
        padding 2px 4px
        margin 2px
        m-font-size(11)
        font-weight 800
        background-color: rgba($regent-gray, 0.2)
        color: $cloud-burst
        // border solid 1px #777
        cursor pointer
        &.active
          background-color: $cloud-burst
          box-shadow rgba(100, 121, 145, 0.25) 5px 5px 20px 0px
          color: #fff
</style>